import React, { useEffect, useState, Fragment } from 'react';
import { Button, useMediaQuery, IconButton } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { useSelector } from 'react-redux';
import FormText from '../../components/FormText/FormText';
import useSummaryStyles from './summaryStepStyles';
import { handleEditClick } from './summaryFunctions';
import { INPUT_TYPES, TEXT_TYPES } from '../../constants/formElements';
import FilePreview from '../../components/FilePreview/FilePreview';
import 'datejs';
import { getTranslation } from '../../utils/functions';
import { formatData } from './useBuildSummaryFormConstants';
import {
  registerDataSelector,
  stepsSelector,
} from '../../store/selectors/formConfigSelectors';
import {
  formInitialValuesSelector,
  languageSelector,
} from '../../store/selectors/appSelectors';
import {
  eventsListSelector,
  formSessionDropdownListSelector,
} from '../../store/selectors/formSessionSelectors';
import ReadOnlyText from '../../components/Inputs/ReadOnlyText/ReadOnlyText';

const buildSummaryStep = (
  fieldsData,
  steps,
  classes,
  isMobileView,
  eventsList,
  formSessionDropdownList,
) => {
  const stepsSummary = [];

  steps.forEach(({ title, sections }, stepIndex) => {
    const titleElement = (
      <FormText
        // eslint-disable-next-line react/no-array-index-key
        key={`title-${stepIndex}`}
        className={classes.title}
        text={title}
        type={TEXT_TYPES.SECTION_HEADER}
      />
    );

    const boardContent = [];

    (sections ?? []).forEach(
      ({ title: subTitle, elements, dependsOnFieldCode }, sectionIndex) => {
        if (subTitle && dependsOnFieldCode && !fieldsData[dependsOnFieldCode]) {
          return;
        }
        const subTitleElement = (
          <FormText
            // eslint-disable-next-line react/no-array-index-key
            key={`subTitle-${sectionIndex}`}
            className={classes.subTitle}
            text={subTitle}
            type={TEXT_TYPES.SUBSECTION_TEXT}
          />
        );

        const elementsSummary = (elements ?? []).map(
          ({
            properties: { label, values, useApiForOptions },
            fieldCode,
            typeCode,
          }) => {
            if (!fieldCode) return null;

            const providedValue = fieldsData[fieldCode];

            if (
              typeCode === INPUT_TYPES.FILE_UPLOAD_SINGLE ||
              typeCode === INPUT_TYPES.FILE_UPLOAD_SINGLE_WITH_WEBCAM
            )
              return (
                <div className={classes.fieldContainer} key={fieldCode}>
                  <FilePreview previewMode file={providedValue} />
                </div>
              );

            if (typeCode === INPUT_TYPES.READ_ONLY_TEXT) {
              return (
                <div className={classes.fieldContainer} key={fieldCode}>
                  <ReadOnlyText defaultText={getTranslation(label)} />
                </div>
              );
            }

            return (
              <div className={classes.fieldContainer} key={fieldCode}>
                <label className={classes.label} htmlFor={fieldCode}>
                  {getTranslation(label)}
                </label>
                <span className={classes.providedData}>
                  {formatData({
                    fieldCode,
                    providedValue,
                    typeCode,
                    values,
                    eventsList,
                    fieldsData,
                    formSessionDropdownList,
                    useApiForOptions,
                  })}
                </span>
              </div>
            );
          },
        );

        const sectionElement = (
          // eslint-disable-next-line react/no-array-index-key
          <Fragment key={`section-${sectionIndex}`}>
            {subTitleElement}
            {elementsSummary}
          </Fragment>
        );

        boardContent.push(sectionElement);
      },
    );

    const editButton = (
      <>
        {isMobileView ? (
          <IconButton
            onClick={() => handleEditClick(stepIndex)}
            className={classes.editButton}
            aria-label="edit"
          >
            <EditIcon color="primary" />
          </IconButton>
        ) : (
          <Button
            onClick={() => handleEditClick(stepIndex)}
            className={classes.editButton}
            color="primary"
          >
            {getTranslation('BUTTON_EDIT')}
          </Button>
        )}
      </>
    );

    const board = (
      // eslint-disable-next-line react/no-array-index-key
      <section key={`board-${stepIndex}`} className={classes.board}>
        {titleElement}
        {editButton}
        {boardContent}
      </section>
    );

    stepsSummary.push(board);
  });

  return stepsSummary;
};

const useBuildSummaryForm = () => {
  const {
    registerData,
    steps,
    formInitialValues,
    language,
    eventsList,
    formSessionDropdownList,
  } = useSelector((state) => ({
    // formConfig state
    registerData: registerDataSelector(state),
    steps: stepsSelector(state),
    // app state
    formInitialValues: formInitialValuesSelector(state),
    language: languageSelector(state),
    eventsList: eventsListSelector(state),
    formSessionDropdownList: formSessionDropdownListSelector(state),
  }));

  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const classes = useSummaryStyles();

  const [summarySteps, setSummarySteps] = useState([]);

  useEffect(() => {
    const fieldsData = {
      ...formInitialValues,
      ...registerData.reduce((acc, current) => ({ ...acc, ...current }), {}),
    };

    setSummarySteps(
      buildSummaryStep(
        fieldsData,
        steps,
        classes,
        isMobileView,
        eventsList,
        formSessionDropdownList,
      ),
    );
  }, [registerData, formInitialValues, steps, classes, isMobileView, language]);

  return { summarySteps };
};

export default useBuildSummaryForm;
